import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MachineStateOption {
  id: number;
  valueLocale: string;
  color: string;
  subcategory: {
    id: number;
    valueLocale: string;
    category: { id: number; valueLocale: string };
  };
}

/**
 * Cascading picker over the 3-level machine state hierarchy (ADR-015):
 * category -> subcategory -> state (leaf). Shared by the FERMO modal and the
 * state history page, which must both let the operator walk all three levels.
 */
export default function StatePicker({
  states,
  value,
  onChange,
  selectClassName,
  showLabels = false,
  stacked = true,
}: {
  states: MachineStateOption[];
  value: number | null;
  onChange: (machineStateId: number | null) => void;
  selectClassName: string;
  showLabels?: boolean;
  stacked?: boolean;
}) {
  const { t } = useTranslation();

  const selected = useMemo(() => states.find(s => s.id === value) ?? null, [states, value]);

  const [categoryId, setCategoryId] = useState<number | null>(selected?.subcategory.category.id ?? null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(selected?.subcategory.id ?? null);

  // Follow the selection when the parent changes it (e.g. rows reloaded after a save)
  useEffect(() => {
    if (!selected) return;
    setCategoryId(selected.subcategory.category.id);
    setSubcategoryId(selected.subcategory.id);
  }, [selected]);

  const categories = useMemo(() => {
    const map = new Map<number, { id: number; label: string }>();
    for (const s of states) {
      map.set(s.subcategory.category.id, { id: s.subcategory.category.id, label: s.subcategory.category.valueLocale });
    }
    return Array.from(map.values());
  }, [states]);

  const subcategories = useMemo(() => {
    const map = new Map<number, { id: number; label: string }>();
    for (const s of states) {
      if (s.subcategory.category.id !== categoryId) continue;
      map.set(s.subcategory.id, { id: s.subcategory.id, label: s.subcategory.valueLocale });
    }
    return Array.from(map.values());
  }, [states, categoryId]);

  const leaves = useMemo(
    () => states.filter(s => s.subcategory.id === subcategoryId),
    [states, subcategoryId],
  );

  const field = (label: string, select: React.ReactNode) => (
    <div className={stacked ? '' : 'flex-1 min-w-[9rem]'}>
      {showLabels && <label className="block text-gray-300 font-bold text-sm mb-2">{label}</label>}
      {select}
    </div>
  );

  return (
    <div className={stacked ? 'space-y-4' : 'flex flex-wrap gap-2'}>
      {field(t('fermo.category'), (
        <select
          className={selectClassName}
          value={categoryId ?? ''}
          onChange={e => {
            setCategoryId(e.target.value ? Number(e.target.value) : null);
            setSubcategoryId(null);
            onChange(null);
          }}
        >
          <option value="" disabled>{t('fermo.select')}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      ))}

      {field(t('fermo.subcategory'), (
        <select
          className={selectClassName}
          value={subcategoryId ?? ''}
          disabled={categoryId === null}
          onChange={e => {
            setSubcategoryId(e.target.value ? Number(e.target.value) : null);
            onChange(null);
          }}
        >
          <option value="" disabled>{t('fermo.select')}</option>
          {subcategories.map(sc => <option key={sc.id} value={sc.id}>{sc.label}</option>)}
        </select>
      ))}

      {field(t('fermo.reason'), (
        <select
          className={selectClassName}
          value={value ?? ''}
          disabled={subcategoryId === null}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="" disabled>{t('fermo.select')}</option>
          {leaves.map(s => <option key={s.id} value={s.id}>{s.valueLocale}</option>)}
        </select>
      ))}
    </div>
  );
}
